import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";

/* ─────────────────────────────────────────────
   Inline styles keep this a single drop-in file
   matching the Tutorlinks / blue-accent palette
───────────────────────────────────────────── */
const S = {
  /* ── Upload placeholder card ── */
  uploadCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: 160,
    height: 130,
    border: "1.5px dashed #c8d6e5",
    borderRadius: 12,
    cursor: "pointer",
    background: "#fff",
    gap: 8,
    flexShrink: 0,
    transition: "border-color .2s, background .2s",
  },
  uploadCardHover: {
    borderColor: "#3b82f6",
    background: "#f0f7ff",
  },
  uploadIconWrap: {
    width: 52,
    height: 52,
    border: "1.5px solid #c8d6e5",
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  plusBadge: {
    position: "absolute",
    bottom: -6,
    right: -6,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#3b82f6",
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  uploadLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
    textAlign: "center",
  },
  uploadSub: {
    fontSize: 10,
    color: "#64748b",
    margin: 0,
    textAlign: "center",
    lineHeight: 1.4,
    maxWidth: 130,
  },

  /* ── Video thumbnail card ── */
  videoCard: {
    width: 160,
    flexShrink: 0,
    cursor: "pointer",
  },
  videoThumb: {
    width: 160,
    height: 110,
    borderRadius: 10,
    objectFit: "cover",
    background: "#0f172a",
    display: "block",
    position: "relative",
  },
  videoThumbWrap: {
    position: "relative",
    width: 160,
    height: 110,
    borderRadius: 10,
    overflow: "hidden",
    background: "#0f172a",
  },
  playBtn: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(0,0,0,.28)",
  },
  playCircle: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "rgba(255,255,255,.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  videoInfo: { padding: "6px 2px 0" },
  videoTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#1e293b",
    margin: "0 0 2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 156,
  },
  videoMeta: {
    fontSize: 10,
    color: "#64748b",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  moreBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    padding: 0,
    fontSize: 16,
    lineHeight: 1,
  },
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 2,
  },

  /* ── Grid wrapper ── */
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 16,
  },

  /* ── Overlay backdrop ── */
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(2px)",
  },

  /* ── Base modal ── */
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: "28px 28px 24px",
    width: 420,
    maxWidth: "92vw",
    boxShadow: "0 24px 60px rgba(15,23,42,.18)",
    position: "relative",
    animation: "slideUp .22s ease",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 18px",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    fontSize: 20,
    color: "#94a3b8",
    cursor: "pointer",
    lineHeight: 1,
  },

  /* ── Form elements ── */
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
    marginBottom: 6,
  },
  required: { color: "#ef4444", marginLeft: 2 },
  input: {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13,
    color: "#0f172a",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color .15s",
  },
  dropZone: {
    border: "1.5px dashed #93c5fd",
    borderRadius: 10,
    background: "#f0f7ff",
    padding: "28px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    cursor: "pointer",
    marginTop: 6,
    transition: "background .2s, border-color .2s",
  },
  dropZoneActive: {
    background: "#dbeafe",
    borderColor: "#3b82f6",
  },
  dropIcon: { color: "#3b82f6", marginBottom: 4 },
  dropText: { fontSize: 13, color: "#334155", margin: 0, textAlign: "center" },
  dropSub: { fontSize: 11, color: "#94a3b8", margin: 0 },
  browseLink: { color: "#3b82f6", textDecoration: "underline", cursor: "pointer" },
  fileName: {
    fontSize: 12,
    color: "#3b82f6",
    marginTop: 6,
    textAlign: "center",
    wordBreak: "break-all",
  },

  /* ── Modal action row ── */
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 22,
  },
  cancelBtn: {
    padding: "9px 22px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
    transition: "background .15s",
  },
  uploadBtn: {
    padding: "9px 22px",
    border: "none",
    borderRadius: 8,
    background: "#3b82f6",
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    transition: "background .15s",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  uploadBtnDisabled: {
    background: "#93c5fd",
    cursor: "not-allowed",
  },

  /* ── Success / Failure modal ── */
  resultModal: {
    background: "#fff",
    borderRadius: 14,
    padding: "36px 32px 28px",
    width: 320,
    maxWidth: "88vw",
    boxShadow: "0 24px 60px rgba(15,23,42,.18)",
    textAlign: "center",
    animation: "slideUp .22s ease",
  },
  resultIcon: { marginBottom: 14 },
  resultTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 8px",
  },
  resultSub: { fontSize: 13, color: "#64748b", margin: "0 0 22px", lineHeight: 1.55 },
  okBtn: {
    padding: "9px 32px",
    border: "none",
    borderRadius: 8,
    background: "#3b82f6",
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
  },
};

/* ── tiny keyframe injection ── */
if (typeof document !== "undefined" && !document.getElementById("ms-anim")) {
  const style = document.createElement("style");
  style.id = "ms-anim";
  style.textContent = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
  `;
  document.head.appendChild(style);
}

/* ════════════════════════════════════════════
   Sub-components
════════════════════════════════════════════ */

function UploadPlaceholder({ onClick, hovered, onMouseEnter, onMouseLeave }) {
  return (
    <div
      style={{ ...S.uploadCard, ...(hovered ? S.uploadCardHover : {}) }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      aria-label="Upload video"
    >
      <div style={S.uploadIconWrap}>
        {/* monitor icon */}
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        <span style={S.plusBadge}>+</span>
      </div>
      <p style={S.uploadLabel}>Upload Video</p>
      <p style={S.uploadSub}>Supports: MP4, MOV, M4V<br />Intro Videos, Lessons Demo,<br />Virtual Centre Tours</p>
    </div>
  );
}

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

// In VideoCard inside MediaSection.jsx — same fix:
const toggle = async () => {
  if (!videoRef.current) return;
  if (playing) {
    videoRef.current.pause();
    setPlaying(false);
  } else {
    try {
      await videoRef.current.play();
      setPlaying(true);
    } catch (err) {
      if (err.name !== "AbortError") console.error("Video play error:", err);
      setPlaying(false);
    }
  }
};

  return (
    <div style={S.videoCard}>
      <div style={S.videoThumbWrap} onClick={toggle}>
        <video
          ref={videoRef}
          src={video.url}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onEnded={() => setPlaying(false)}
        />
        {!playing && (
          <div style={S.playBtn}>
            <div style={S.playCircle}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="#1e293b">
                <path d="M3 2l9 5-9 5V2z" />
              </svg>
            </div>
          </div>
        )}
      </div>
      <div style={S.videoInfo}>
        <p style={S.videoTitle}>{video.title}</p>
        <div style={S.metaRow}>
          <p style={S.videoMeta}>
            <span>Uploaded {video.uploadedAt}</span>
          </p>
          <button style={S.moreBtn} aria-label="More options">⋯</button>
        </div>
        <p style={{ ...S.videoMeta, marginTop: 2 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
          </svg>
          {video.views} Viewed
        </p>
      </div>
    </div>
  );
}

/* ── Upload modal ── */
function UploadModal({ onClose, onSuccess, onError }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const canUpload = title.trim() && file && !loading;

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type.startsWith("video/")) setFile(dropped);
  };

  const handleUpload = async () => {
    if (!canUpload) return;
    setLoading(true);
    try {
      // Pass a plain object — updateUserProfile builds FormData internally
      const data = await updateUserProfile({
        video: file,        // File object → appended as multipart by the util
        videoTitle: title,  // string → appended as req.body.videoTitle
      });
      // grab the last saved video entry returned from the server
      const saved = data?.user?.videos?.at(-1);
      onSuccess({ title, file, serverUrl: saved?.url ?? null, id: saved?._id ?? null });
    } catch (err) {
      onError(err?.message || "Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <button style={S.closeBtn} onClick={onClose} aria-label="Close">×</button>
        <p style={S.modalTitle}>Upload Video</p>

        {/* Title */}
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>
            Video Title<span style={S.required}>*</span>
          </label>
          <input
            style={S.input}
            placeholder="Enter Course Intro / Lesson Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
            onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
          />
        </div>

        {/* Drop zone */}
        <label style={S.label}>Upload Video Content</label>
        <div
          style={{ ...S.dropZone, ...(dragging ? S.dropZoneActive : {}) }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <svg style={S.dropIcon} width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <p style={S.dropText}>
            Drag &amp; drop files or{" "}
            <span style={S.browseLink}>Browse</span>
          </p>
          <p style={S.dropSub}>Supported formats: MP4, MOV, M4V, MKV, WMV</p>
          {file && <p style={S.fileName}>✓ {file.name}</p>}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="video/*"
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
        </div>

        {/* Actions */}
        <div style={S.actionRow}>
          <button style={S.cancelBtn} onClick={onClose}>Cancel</button>
          <button
            style={{ ...S.uploadBtn, ...(!canUpload ? S.uploadBtnDisabled : {}) }}
            onClick={handleUpload}
            disabled={!canUpload}
          >
            {loading && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" style={{ animation: "spin 1s linear infinite" }}>
                <path d="M21 12a9 9 0 1 1-9-9" strokeLinecap="round" />
              </svg>
            )}
            {loading ? "Uploading…" : "Upload!"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Result modal (success / error) ── */
function ResultModal({ type, message, onClose }) {
  const isSuccess = type === "success";
  return (
    <div style={S.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.resultModal}>
        <div style={S.resultIcon}>
          {isSuccess ? (
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="28" fill="#eff6ff" />
              <circle cx="28" cy="28" r="20" fill="#3b82f6" opacity=".15" />
              <path d="M18 28l7 7 13-14" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          ) : (
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="28" fill="#fef2f2" />
              <circle cx="28" cy="28" r="20" fill="#ef4444" opacity=".15" />
              <path d="M20 20l16 16M36 20l-16 16" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" fill="none" />
            </svg>
          )}
        </div>
        <p style={S.resultTitle}>{isSuccess ? "Uploaded!!!" : "Upload Failed"}</p>
        <p style={S.resultSub}>
          {isSuccess
            ? "Great news! Your video has been uploaded successfully and is now ready for viewing."
            : message || "Something went wrong. Please try again."}
        </p>
        <button style={{ ...S.okBtn, ...(isSuccess ? {} : { background: "#ef4444" }) }} onClick={onClose}>
          {isSuccess ? "Done" : "Try Again"}
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════
   Main component
════════════════════════════════════════════ */
const MediaSection = () => {
  const { user } = useAuth();

  const [videos, setVideos] = useState([]);
console.log(user);
  // Load existing videos from the server on mount
  useEffect(() => {
    if (user?.videos?.length) {
      setVideos(
        user.videos.map((v) => ({
          id: v._id,
          title: v.title,
          url: `${process.env?.REACT_APP_API_URL ?? ""}${v.url}`,
          uploadedAt: new Date(v.uploadedAt).toLocaleDateString(),
          views: v.views ?? 0,
        }))
      );
    }
  }, [user]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', message? }
  const [cardHovered, setCardHovered] = useState(false);

  const handleSuccess = ({ title, file, serverUrl, id }) => {
    // Prefer the real server URL so the video persists on refresh;
    // fall back to a local object-URL for instant preview.
    const url = serverUrl
      ? `${process.env?.REACT_APP_API_URL ?? ""}${serverUrl}`
      : URL.createObjectURL(file);
    setVideos((prev) => [
      ...prev,
      {
        id: id ?? Date.now(),
        title,
        url,
        uploadedAt: "Just now",
        views: 0,
      },
    ]);
    setShowUploadModal(false);
    setResult({ type: "success" });
  };

  const handleError = (msg) => {
    setShowUploadModal(false);
    setResult({ type: "error", message: msg });
  };

  return (
    <>
      {/* ── grid ── */}
      <div style={S.grid}>
        {videos.map((vid) => (
          <VideoCard key={vid.id} video={vid} />
        ))}

        <UploadPlaceholder
          onClick={() => setShowUploadModal(true)}
          hovered={cardHovered}
          onMouseEnter={() => setCardHovered(true)}
          onMouseLeave={() => setCardHovered(false)}
        />
      </div>

      {/* ── upload modal ── */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      )}

      {/* ── result modal ── */}
      {result && (
        <ResultModal
          type={result.type}
          message={result.message}
          onClose={() => setResult(null)}
        />
      )}
    </>
  );
};

export default MediaSection;
